import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, Users, Wallet, Sparkles, HandHeart, TrendingUp, LogOut,
  ShieldCheck, ExternalLink, MapPin, Filter, CheckCircle2, Clock, XCircle,
  Star, MessageSquare, Trash2, History, Globe, IdCard, Eye, Check, X, KeyRound,
  Lock, Download, Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { adminApi, adminTokenStorage } from '../lib/adminApi';
import { useToast } from '../hooks/use-toast';

const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

function StatCard({ Icon, label, value, color, testId }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 card-lift" data-testid={testId}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3 text-[12px] uppercase tracking-wider text-neutral-500 font-medium">{label}</div>
      <div className="mt-1 text-[24px] font-semibold text-[#1a1d1a] tracking-tight">{value}</div>
    </div>
  );
}

function CagnotteRow({ c, onDelete }) {
  const isYako = c.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';
  const pct = Math.min(100, Math.round(((c.raised || 0) / (c.goal || 1)) * 100));
  return (
    <div className="flex gap-4 rounded-2xl bg-white border border-black/5 p-4 hover:bg-neutral-50/60 transition-colors" data-testid={`admin-cagnotte-${c.id}`}>
      <div className="w-20 h-20 rounded-xl overflow-hidden bg-neutral-100 shrink-0">
        {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: accent }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white" style={{ background: accent }}>{c.type}</span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{
            background: c.isActive ? '#10b98120' : '#94a3b820',
            color: c.isActive ? '#047857' : '#475569',
          }}>{c.isActive ? 'Active' : 'Terminée'}</span>
          <span className="text-[11.5px] text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.country}</span>
        </div>
        <h3 className="mt-1 font-semibold text-[15px] text-[#1a1d1a] truncate">{c.title}</h3>
        <div className="mt-0.5 text-[11.5px] text-neutral-500 truncate">
          Par <span className="text-neutral-700 font-medium">{c.ownerName || 'Sans nom'}</span>
          {c.ownerPhone && <span className="text-neutral-500"> · {c.ownerPhone}</span>}
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[12px] text-neutral-600">
          <span><span className="font-semibold text-neutral-900">{formatN(c.raised || 0)}</span> / {formatN(c.goal)} FCFA · {c.donors || 0} donateurs</span>
          <Link to={`/cagnotte/${c.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#d97757] font-medium hover:gap-1.5 transition-all">
            Ouvrir <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <div className="mt-2">
          <button
            type="button"
            data-testid={`admin-delete-btn-${c.id}`}
            onClick={() => onDelete(c)}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-3 h-3" /> Supprimer (Admin)
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedbackRow({ f }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-4" data-testid={`admin-feedback-${f.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-3.5 h-3.5 ${f.rating >= n ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-neutral-300'}`} />
            ))}
          </div>
          <span className="text-[13px] font-medium text-neutral-800">{f.userName || 'Anonyme'}</span>
        </div>
        <span className="text-[11.5px] text-neutral-500">
          {f.createdAt ? new Date(f.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
          {f.page && <span className="ml-2 text-neutral-400">{f.page}</span>}
        </span>
      </div>
      {f.comment && <p className="mt-2 text-[13px] text-neutral-600 leading-snug">“{f.comment}”</p>}
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [cagnottes, setCagnottes] = useState([]);
  const [feedbacks, setFeedbacks] = useState({ items: [], averageRating: null });
  const [extended, setExtended] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | Joyeuse | Yako
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | ended

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/cagnottes/${deleteTarget.id}`);
      setCagnottes((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      toast({ title: 'Cagnotte supprimée', description: `« ${deleteTarget.title} » supprimée. Le créateur a été notifié.` });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible.' });
    } finally { setDeleting(false); }
  };

  const [smsStats, setSmsStats] = useState(null);
  const [smsLogs, setSmsLogs] = useState([]);
  const [showSmsLogs, setShowSmsLogs] = useState(false);
  const [audit, setAudit] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [donsPrives, setDonsPrives] = useState({ items: [], totalAmount: 0, totalFees: 0 });
  const [contactMsgs, setContactMsgs] = useState({ items: [], newCount: 0 });
  const [services, setServices] = useState([]);
  const [resetStep, setResetStep] = useState(false);
  const [resetForm, setResetForm] = useState({ code: '', pw: '' });
  const [resetBusy, setResetBusy] = useState(false);
  const [backupRemaining, setBackupRemaining] = useState(null);
  const [backupGen, setBackupGen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, f, x, sm, au, ky, bc, dp, cm, sv, sl] = await Promise.all([
        adminApi.get('/admin/stats'),
        adminApi.get('/admin/cagnottes'),
        adminApi.get('/admin/feedbacks').catch(() => ({ data: { items: [], averageRating: null } })),
        adminApi.get('/admin/stats/extended').catch(() => ({ data: null })),
        adminApi.get('/admin/sms/stats').catch(() => ({ data: null })),
        adminApi.get('/admin/audit').catch(() => ({ data: { items: [] } })),
        adminApi.get('/admin/kyc').catch(() => ({ data: { items: [] } })),
        adminApi.get('/admin/backup-codes/status').catch(() => ({ data: { remaining: null } })),
        adminApi.get('/admin/dons-prives').catch(() => ({ data: { items: [], totalAmount: 0, totalFees: 0 } })),
        adminApi.get('/admin/contact-messages').catch(() => ({ data: { items: [], newCount: 0 } })),
        adminApi.get('/admin/services-status').catch(() => ({ data: { services: [] } })),
        adminApi.get('/admin/sms/logs?limit=30').catch(() => ({ data: { items: [] } })),
      ]);
      setSmsStats(sm.data);
      setSmsLogs(sl.data.items || []);
      setStats(s.data);
      setCagnottes(c.data.items || []);
      setFeedbacks({ items: f.data.items || [], averageRating: f.data.averageRating });
      setExtended(x.data);
      setAudit(au.data.items || []);
      setKyc(ky.data.items || []);
      setBackupRemaining(bc.data.remaining);
      setDonsPrives(dp.data || { items: [], totalAmount: 0, totalFees: 0 });
      setContactMsgs(cm.data || { items: [], newCount: 0 });
      setServices(sv.data.services || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        adminTokenStorage.clear();
        navigate('/admin/login', { replace: true });
      } else {
        toast({ title: 'Erreur', description: 'Impossible de charger les données admin.' });
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!adminTokenStorage.get()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleContactStatus = async (m) => {
    const next = m.status === 'new' ? 'traite' : 'new';
    try {
      await adminApi.post(`/admin/contact-messages/${m.id}/status`, { status: next });
      setContactMsgs((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.id === m.id ? { ...it, status: next } : it)),
        newCount: prev.newCount + (next === 'traite' ? -1 : 1),
      }));
    } catch (e) {
      toast({ title: 'Erreur', description: 'Mise à jour du statut impossible.' });
    }
  };

  const requestReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await adminApi.post('/admin/password-reset/request');
      setResetStep(true);
      toast({ title: 'Code envoyé 📧', description: `Un code de confirmation a été envoyé à ${data.emailHint}.` });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || "Envoi du code impossible." });
    } finally { setResetBusy(false); }
  };

  const confirmReset = async () => {
    if (!resetForm.code || !resetForm.pw) { toast({ title: 'Champs requis', description: 'Saisissez le code et le nouveau mot de passe.' }); return; }
    setResetBusy(true);
    try {
      await adminApi.post('/admin/password-reset/confirm', { code: resetForm.code, newPassword: resetForm.pw, totp: resetForm.totp || undefined });
      setResetStep(false);
      setResetForm({ code: '', pw: '' });
      toast({ title: 'Mot de passe réinitialisé ✅', description: 'Utilisez-le dès votre prochaine connexion.' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Réinitialisation impossible.' });
    } finally { setResetBusy(false); }
  };

  const exportDonsPrives = async () => {
    try {
      const res = await adminApi.get('/admin/dons-prives/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dons_prives.csv';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Export CSV impossible.' });
    }
  };

  const viewKycDoc = async (docId) => {
    try {
      const res = await adminApi.get(`/admin/kyc/documents/${docId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible d\u2019ouvrir le document.' });
    }
  };

  const decideKyc = async (cagnotteId, decision) => {
    try {
      await adminApi.post(`/admin/kyc/${cagnotteId}/${decision}`);
      toast({ title: decision === 'approve' ? 'KYC validé' : 'KYC refusé', description: 'Les documents ont été supprimés.' });
      setKyc((prev) => prev.filter((k) => k.cagnotteId !== cagnotteId));
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Action impossible.' });
    }
  };

  const generateBackupCodes = async () => {
    if (backupGen) return;
    setBackupGen(true);
    try {
      const { data } = await adminApi.post('/admin/backup-codes');
      setBackupCodes(data.codes);
      setBackupRemaining(data.count);
      toast({ title: 'Codes de secours générés', description: 'Copiez-les et conservez-les en lieu sûr — ils ne seront plus affichés.' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible de générer les codes.' });
    } finally { setBackupGen(false); }
  };

  const changePassword = async () => {
    if (pwdSaving) return;
    if (!pwd.current || !pwd.next) { toast({ title: 'Champs requis', description: 'Renseignez le mot de passe actuel et le nouveau.' }); return; }
    if (pwd.next !== pwd.confirm) { toast({ title: 'Non conforme', description: 'Les deux nouveaux mots de passe ne correspondent pas.' }); return; }
    setPwdSaving(true);
    try {
      await adminApi.post('/admin/change-password', { currentPassword: pwd.current, newPassword: pwd.next, totp: pwd.totp || undefined });
      toast({ title: 'Mot de passe mis à jour', description: 'Votre nouveau mot de passe est actif.' });
      setPwd({ current: '', next: '', confirm: '', totp: '' });
    } catch (e) {
      toast({ title: 'Échec', description: e?.response?.data?.detail || 'Impossible de changer le mot de passe.' });
    } finally { setPwdSaving(false); }
  };

  const logout = () => {
    adminTokenStorage.clear();
    navigate('/admin/login', { replace: true });
  };

  const filtered = cagnottes.filter((c) => {
    if (typeFilter !== 'all' && c.type !== typeFilter) return false;
    if (statusFilter === 'active' && !c.isActive) return false;
    if (statusFilter === 'ended' && c.isActive) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = `${c.title || ''} ${c.ownerName || ''} ${c.ownerPhone || ''} ${c.country || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#f5f4ef]" data-testid="admin-dashboard-page">
      <header className="bg-white border-b border-black/5">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1a1d1a] text-white flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10.5px] tracking-[0.18em] uppercase font-medium text-neutral-500">Fodindin</div>
              <h1 className="text-[16px] font-semibold text-[#1a1d1a]">Console administrateur</h1>
            </div>
          </div>
          <Button data-testid="admin-logout-btn" onClick={logout} variant="ghost" className="btn-anim rounded-full h-10 px-4 text-[13px]">
            <LogOut className="w-4 h-4 mr-1.5" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading || !stats ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline-block text-neutral-500" /></div>
        ) : (
          <>
            <section>
              <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Statistiques globales</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard testId="stat-users" Icon={Users} label="Utilisateurs" value={formatN(stats.usersCount)} color="#0e3a3a" />
                <StatCard testId="stat-cagnottes" Icon={Sparkles} label="Cagnottes" value={formatN(stats.cagnottesCount)} color="#d97757" />
                <StatCard testId="stat-active" Icon={TrendingUp} label="Actives" value={formatN(stats.activeCagnottes)} color="#5a8268" />
                <StatCard testId="stat-raised" Icon={Wallet} label="Total collecté" value={`${formatN(stats.totalRaised)} FCFA`} color="#1a1d1a" />
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard testId="stat-joyeuse" Icon={Sparkles} label="Joyeuses" value={formatN(stats.joyeuseCount)} color="#d97757" />
                <StatCard testId="stat-yako" Icon={HandHeart} label="Yako" value={formatN(stats.yakoCount)} color="#5a8268" />
                <StatCard testId="stat-donations" Icon={CheckCircle2} label="Dons réussis" value={formatN(stats.donationsSuccess)} color="#10b981" />
                <StatCard testId="stat-donations-pending" Icon={Clock} label="Dons en attente" value={formatN(stats.donationsPending)} color="#f59e0b" />
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard testId="stat-goal" Icon={TrendingUp} label="Objectifs cumulés" value={`${formatN(stats.totalGoal)} FCFA`} color="#0e3a3a" />
                <StatCard testId="stat-donations-total" Icon={Wallet} label="Montants versés" value={`${formatN(stats.donationsTotal)} FCFA`} color="#d97757" />
                <StatCard testId="stat-donations-failed" Icon={XCircle} label="Dons échoués" value={formatN(stats.donationsFailed)} color="#ef4444" />
                <StatCard testId="stat-invites" Icon={Users} label="Invitations" value={formatN(stats.invitesCount)} color="#5a8268" />
              </div>

              {smsStats && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard testId="stat-sms-rate" Icon={TrendingUp} label="Taux délivrance SMS" value={smsStats.deliveryRate == null ? '—' : `${smsStats.deliveryRate}%`} color="#0e3a3a" />
                  <StatCard testId="stat-sms-total" Icon={Sparkles} label="SMS envoyés" value={formatN(smsStats.total)} color="#d97757" />
                  <StatCard testId="stat-sms-delivered" Icon={CheckCircle2} label="SMS délivrés" value={formatN(smsStats.delivered)} color="#10b981" />
                  <StatCard testId="stat-sms-failed" Icon={XCircle} label="SMS échoués" value={formatN(smsStats.failed)} color="#ef4444" />
                </div>
              )}
              {smsStats?.byProvider && Object.keys(smsStats.byProvider).length > 0 && (
                <div data-testid="sms-provider-breakdown" className="mt-2 text-[12px] text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
                  {Object.entries(smsStats.byProvider).map(([prov, s]) => (
                    <span key={prov}>
                      <span className="font-medium text-neutral-700">{prov === 'smsala' ? 'SMSALA' : "Africa's Talking"}</span>
                      {' '}: {formatN(s.total)} envoyés · {formatN(s.delivered)} délivrés · {formatN(s.failed)} échoués
                    </span>
                  ))}
                </div>
              )}
              {smsLogs.length > 0 && (
                <div className="mt-3">
                  <button
                    data-testid="toggle-sms-logs-btn"
                    onClick={() => setShowSmsLogs((v) => !v)}
                    className="text-[12.5px] font-medium text-[#d97757] hover:underline"
                  >
                    {showSmsLogs ? 'Masquer' : 'Afficher'} l'historique des SMS ({smsLogs.length})
                  </button>
                  {showSmsLogs && (
                    <div data-testid="sms-logs-table" className="mt-2 rounded-2xl bg-white border border-black/5 overflow-x-auto">
                      <table className="w-full text-[12px]">
                        <thead>
                          <tr className="text-left text-neutral-400 uppercase tracking-wider text-[10.5px]">
                            <th className="px-4 py-2.5">Date</th>
                            <th className="px-4 py-2.5">Numéro</th>
                            <th className="px-4 py-2.5">Opérateur</th>
                            <th className="px-4 py-2.5">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {smsLogs.map((l, i) => (
                            <tr key={l.messageId || i} className="border-t border-black/5 text-neutral-700">
                              <td className="px-4 py-2 whitespace-nowrap">{l.createdAt ? new Date(l.createdAt).toLocaleString('fr-FR') : '—'}</td>
                              <td className="px-4 py-2">{l.phone || '—'}</td>
                              <td className="px-4 py-2">{l.provider === 'smsala' ? 'SMSALA' : 'AT'}</td>
                              <td className="px-4 py-2">
                                <span className={
                                  ['Success', 'Delivered', 'DELIVRD'].includes(l.status) ? 'text-emerald-600 font-medium'
                                    : ['Failed', 'Rejected', 'Undelivered'].includes(l.status) ? 'text-red-600 font-medium'
                                      : 'text-neutral-500'
                                }>
                                  {l.status || '—'}{l.failureReason ? ` (${l.failureReason})` : ''}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="mt-10">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Toutes les cagnottes ({filtered.length})</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Filter className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      data-testid="admin-search-input"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Rechercher (titre, tel, nom)…"
                      className="pl-9 h-9 rounded-full text-[13px] w-[220px] bg-white"
                    />
                  </div>
                  <select
                    data-testid="admin-type-filter"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="h-9 rounded-full px-3 text-[13px] bg-white border border-black/10"
                  >
                    <option value="all">Tous les types</option>
                    <option value="Joyeuse">Joyeuse</option>
                    <option value="Yako">Yako</option>
                  </select>
                  <select
                    data-testid="admin-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 rounded-full px-3 text-[13px] bg-white border border-black/10"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="active">Actives</option>
                    <option value="ended">Terminées</option>
                  </select>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
                  <p className="text-[14px] text-neutral-600">Aucune cagnotte ne correspond aux filtres.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {filtered.map((c) => <CagnotteRow key={c.id} c={c} onDelete={setDeleteTarget} />)}
                </div>
              )}
            </section>

            {extended && (
              <section className="mt-10" data-testid="admin-extended-stats">
                <div className="flex items-center gap-2 flex-wrap">
                  <TrendingUp className="w-4.5 h-4.5 text-[#d97757]" />
                  <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Analyse financière</h2>
                  <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0e3a3a] bg-[#0e3a3a]/5 px-2.5 py-0.5 rounded-full" data-testid="fees-generated-badge">
                    <Wallet className="w-3.5 h-3.5" /> Frais générés : {new Intl.NumberFormat('fr-FR').format(extended.feesGenerated)} FCFA
                  </span>
                </div>

                <div className="mt-4 rounded-2xl bg-white border border-black/5 p-5">
                  <div className="text-[13px] font-semibold text-neutral-700">Évolution des dons (30 derniers jours)</div>
                  {extended.evolution.length === 0 ? (
                    <p className="mt-3 text-[12.5px] text-neutral-500">Aucun don sur la période.</p>
                  ) : (
                    <div className="mt-3 flex items-end gap-1 h-28" data-testid="evolution-chart">
                      {extended.evolution.map((e) => (
                        <div
                          key={e.date}
                          title={`${e.date} : ${new Intl.NumberFormat('fr-FR').format(e.total)} FCFA (${e.count} don${e.count > 1 ? 's' : ''})`}
                          className="flex-1 rounded-t-md bg-[#d97757]/70 hover:bg-[#d97757] transition-colors min-w-[6px]"
                          style={{ height: `${Math.max(6, Math.round((e.total / Math.max(...extended.evolution.map((v) => v.total), 1)) * 100))}%` }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4 grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-white border border-black/5 p-5">
                    <div className="text-[13px] font-semibold text-neutral-700">Top 5 cagnottes</div>
                    <div className="mt-3 space-y-3">
                      {extended.topCagnottes.map((t) => (
                        <div key={t.id}>
                          <div className="flex items-center justify-between text-[12.5px]">
                            <span className="truncate font-medium text-neutral-800">{t.title}</span>
                            <span className="shrink-0 font-semibold text-[#1a1d1a]">{t.pct}%</span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${t.pct}%`, background: t.type === 'Yako' ? '#5a8268' : '#d97757' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white border border-black/5 p-5">
                    <div className="text-[13px] font-semibold text-neutral-700">Derniers dons reçus</div>
                    <div className="mt-2">
                      {extended.latestDonations.map((d, i) => (
                        <div key={`${d.createdAt}-${i}`} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0 text-[12.5px]">
                          <div className="min-w-0 truncate">
                            <span className="font-medium text-neutral-800">{d.donorName}</span>
                            <span className="text-neutral-500"> · {d.cagnotteTitle}</span>
                          </div>
                          <span className="shrink-0 font-semibold text-[#128C4A]">+{new Intl.NumberFormat('fr-FR').format(d.amount)} FCFA</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="mt-10" data-testid="admin-feedbacks-section">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Avis utilisateurs ({feedbacks.items.length})</h2>
                {feedbacks.averageRating != null && (
                  <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#b45309] bg-[#f59e0b]/10 px-2.5 py-0.5 rounded-full">
                    <Star className="w-3.5 h-3.5 fill-[#f59e0b] text-[#f59e0b]" /> {feedbacks.averageRating}/5
                  </span>
                )}
              </div>
              {feedbacks.items.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
                  <p className="text-[14px] text-neutral-600">Aucun avis pour le moment. Les avis envoyés via le chatbot apparaîtront ici.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {feedbacks.items.map((f) => <FeedbackRow key={f.id} f={f} />)}
                </div>
              )}
            </section>

            <section className="mt-10" data-testid="admin-services-section">
              <div className="flex items-center gap-2">
                <Globe className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">État des services</h2>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {services.map((svc) => (
                  <div key={svc.id} data-testid={`service-status-${svc.id}`} className={`rounded-2xl border p-4 bg-white ${svc.ok ? 'border-emerald-200' : 'border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${svc.ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className="text-[12.5px] font-semibold text-[#1a1d1a] leading-tight">{svc.label}</span>
                    </div>
                    <p className={`mt-1.5 text-[11px] leading-snug ${svc.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                      {svc.ok ? '✓ Actif — ' : '✕ Inactif — '}{svc.detail}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10" data-testid="admin-messagerie-section">
              <div className="flex items-center gap-2 flex-wrap">
                <Mail className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Messagerie ({contactMsgs.items.length})</h2>
                {contactMsgs.newCount > 0 && (
                  <span data-testid="admin-messagerie-new-badge" className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-[#d97757] px-2.5 py-0.5 rounded-full">
                    {contactMsgs.newCount} nouveau{contactMsgs.newCount > 1 ? 'x' : ''}
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-[12px] text-neutral-500">Messages reçus via la page /contact (contact@, marketing@, support@, securite@fodindincagnotte.com). Une notification email vous est envoyée à chaque nouveau message.</p>
              {contactMsgs.items.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
                  <p className="text-[14px] text-neutral-600">Aucun message pour le moment.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {contactMsgs.items.map((m) => (
                    <div key={m.id} data-testid="admin-contact-message-row" className={`rounded-2xl border p-4 sm:p-5 ${m.status === 'new' ? 'bg-white border-[#d97757]/30' : 'bg-white/60 border-black/5'}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                            m.service === 'securite' ? 'bg-[#4A148C]/10 text-[#4A148C]'
                              : m.service === 'support' ? 'bg-[#5a8268]/15 text-[#3f5c49]'
                              : m.service === 'marketing' ? 'bg-amber-100 text-amber-800'
                              : 'bg-[#d97757]/15 text-[#b45309]'}`}>
                            {m.service}
                          </span>
                          <span className="text-[13.5px] font-semibold text-[#1a1d1a]">{m.name}</span>
                          <span className="text-[12px] text-neutral-500">{m.email || m.phone || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-neutral-400">{m.createdAt ? new Date(m.createdAt).toLocaleDateString('fr-FR') : ''}</span>
                          <button
                            type="button"
                            data-testid="admin-contact-toggle-status-btn"
                            onClick={() => toggleContactStatus(m)}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${m.status === 'new' ? 'bg-[#1a1d1a] text-white hover:bg-black' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                          >
                            {m.status === 'new' ? 'Marquer traité' : '✓ Traité'}
                          </button>
                        </div>
                      </div>
                      <p className="mt-2.5 text-[13px] text-neutral-700 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-10" data-testid="admin-dons-prives-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Lock className="w-4.5 h-4.5 text-[#d97757]" />
                  <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Dons privés ({donsPrives.items.length})</h2>
                  {donsPrives.items.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#b45309] bg-[#f59e0b]/10 px-2.5 py-0.5 rounded-full">
                      {formatN(donsPrives.totalAmount)} FCFA · frais {formatN(donsPrives.totalFees)} FCFA
                    </span>
                  )}
                </div>
                {donsPrives.items.length > 0 && (
                  <Button type="button" data-testid="admin-export-dons-prives-btn" onClick={exportDonsPrives}
                    className="btn-anim h-9 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[12.5px] px-4">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Exporter CSV
                  </Button>
                )}
              </div>
              <p className="mt-1.5 text-[12px] text-neutral-500">Invisibles publiquement, mais entièrement tracés (donateur, montant, cagnotte). Chaque consultation et export est journalisé.</p>
              {donsPrives.items.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
                  <p className="text-[14px] text-neutral-600">Aucun don privé pour le moment.</p>
                </div>
              ) : (
                <div className="mt-4 rounded-2xl bg-white border border-black/5 overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="text-left text-neutral-500 border-b border-black/5">
                        <th className="px-4 py-3 font-medium">Date</th>
                        <th className="px-4 py-3 font-medium">Donateur</th>
                        <th className="px-4 py-3 font-medium">Téléphone</th>
                        <th className="px-4 py-3 font-medium">Cagnotte</th>
                        <th className="px-4 py-3 font-medium text-right">Montant</th>
                        <th className="px-4 py-3 font-medium text-right">Frais</th>
                        <th className="px-4 py-3 font-medium text-right">Net</th>
                        <th className="px-4 py-3 font-medium">Statut</th>
                        <th className="px-4 py-3 font-medium">Consentement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donsPrives.items.map((d) => (
                        <tr key={d.id} className="border-b border-black/5 last:border-0" data-testid="admin-don-prive-row">
                          <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">{d.createdAt ? new Date(d.createdAt).toLocaleDateString('fr-FR') : '—'}</td>
                          <td className="px-4 py-3 font-medium text-[#1a1d1a]">{d.donorName}</td>
                          <td className="px-4 py-3 font-mono text-[11.5px] text-neutral-600">{d.donorPhone || '—'}</td>
                          <td className="px-4 py-3 max-w-[200px] truncate">{d.cagnotteTitle}</td>
                          <td className="px-4 py-3 text-right font-semibold text-[#1a1d1a] whitespace-nowrap">{formatN(d.amount)} FCFA</td>
                          <td className="px-4 py-3 text-right text-neutral-600 whitespace-nowrap">{formatN(d.fraisPlateforme)}</td>
                          <td className="px-4 py-3 text-right text-neutral-600 whitespace-nowrap">{formatN(d.montantNet)}</td>
                          <td className="px-4 py-3"><span className="text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">{d.status === 'mocked' ? 'Simulé' : d.status}</span></td>
                          <td className="px-4 py-3 whitespace-nowrap" data-testid="admin-don-prive-consent">
                            {d.consentAt ? (
                              <span className="inline-flex flex-col text-[11px] leading-tight">
                                <span className="font-medium text-emerald-700">✓ {new Date(d.consentAt).toLocaleDateString('fr-FR')} {new Date(d.consentAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                                <span className="font-mono text-neutral-400">{d.consentIp || '—'}</span>
                              </span>
                            ) : (
                              <span className="text-[11px] text-neutral-400">Antérieur à la clause</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="mt-10" data-testid="admin-password-section">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Mot de passe administrateur</h2>
              </div>
              <div className="mt-4 rounded-2xl bg-white border border-black/5 p-5 sm:p-6 max-w-[520px]">
                <p className="text-[13px] text-neutral-600 leading-relaxed">Remplacez le mot de passe par défaut par le vôtre. Minimum 10 caractères, avec lettres et chiffres.</p>
                <div className="mt-4 space-y-3">
                  <Input data-testid="admin-current-password" type="password" placeholder="Mot de passe actuel" autoComplete="current-password"
                    value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="h-11 rounded-xl" />
                  <Input data-testid="admin-new-password" type="password" placeholder="Nouveau mot de passe" autoComplete="new-password"
                    value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="h-11 rounded-xl" />
                  <Input data-testid="admin-confirm-password" type="password" placeholder="Confirmer le nouveau mot de passe" autoComplete="new-password"
                    value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="h-11 rounded-xl" />
                  <Input data-testid="admin-totp-password" placeholder="Code Google Authenticator (6 chiffres)" inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                    value={pwd.totp || ''} onChange={(e) => setPwd({ ...pwd, totp: e.target.value })} className="h-11 rounded-xl tracking-widest" />
                </div>
                <Button type="button" data-testid="admin-change-password-btn" onClick={changePassword} disabled={pwdSaving}
                  className="btn-anim mt-4 h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[13.5px] px-5">
                  {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Mettre à jour le mot de passe
                </Button>
                <div className="mt-6 pt-5 border-t border-black/5">
                  <p className="text-[13px] text-neutral-600 leading-relaxed">Mot de passe oublié ou compromis ? Réinitialisez-le sans l'ancien mot de passe — un <b>code de confirmation par email</b> est exigé pour bloquer toute tentative d'intrusion.</p>
                  {!resetStep ? (
                    <Button type="button" data-testid="admin-reset-password-btn" onClick={requestReset} disabled={resetBusy}
                      variant="outline" className="btn-anim mt-3 h-11 rounded-full border-[#d97757] text-[#d97757] hover:bg-[#d97757]/10 text-[13.5px] px-5">
                      {resetBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Réinitialiser le mot de passe
                    </Button>
                  ) : (
                    <div className="mt-3 space-y-3" data-testid="admin-reset-form">
                      <Input data-testid="admin-reset-code" placeholder="Code reçu par email (6 chiffres)" inputMode="numeric" maxLength={6}
                        value={resetForm.code} onChange={(e) => setResetForm({ ...resetForm, code: e.target.value })} className="h-11 rounded-xl tracking-widest" />
                      <Input data-testid="admin-reset-new-password" type="password" placeholder="Nouveau mot de passe" autoComplete="new-password"
                        value={resetForm.pw} onChange={(e) => setResetForm({ ...resetForm, pw: e.target.value })} className="h-11 rounded-xl" />
                      <Input data-testid="admin-reset-totp" placeholder="Code Google Authenticator (6 chiffres)" inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                        value={resetForm.totp || ''} onChange={(e) => setResetForm({ ...resetForm, totp: e.target.value })} className="h-11 rounded-xl tracking-widest" />
                      <div className="flex gap-2">
                        <Button type="button" data-testid="admin-reset-confirm-btn" onClick={confirmReset} disabled={resetBusy}
                          className="btn-anim h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[13.5px] px-5">
                          {resetBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                          Confirmer la réinitialisation
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => { setResetStep(false); setResetForm({ code: '', pw: '' }); }}
                          className="h-11 rounded-full text-neutral-500 text-[13px] px-4">Annuler</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="mt-10" data-testid="admin-backup-codes-section">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Codes de secours</h2>
              </div>
              <div className="mt-4 rounded-2xl bg-white border border-black/5 p-5 sm:p-6 max-w-[520px]">
                <p className="text-[13px] text-neutral-600 leading-relaxed">Générez 8 codes à usage unique pour vous connecter si vous perdez l'accès à votre application d'authentification <b>et</b> à votre email. Chaque code ne fonctionne qu'une fois.</p>
                {backupRemaining !== null && !backupCodes && (
                  <div data-testid="admin-backup-remaining" className={`mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-full ${backupRemaining === 0 ? 'bg-red-50 text-red-600' : backupRemaining <= 2 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    <KeyRound className="w-3.5 h-3.5" />
                    {backupRemaining === 0
                      ? 'Aucun code de secours actif — générez-en maintenant.'
                      : `${backupRemaining} code${backupRemaining > 1 ? 's' : ''} de secours restant${backupRemaining > 1 ? 's' : ''}${backupRemaining <= 2 ? ' — pensez à régénérer' : ''}`}
                  </div>
                )}
                {backupCodes && (
                  <div className="mt-4 grid grid-cols-2 gap-2" data-testid="admin-backup-codes-list">
                    {backupCodes.map((c) => (
                      <div key={c} className="font-mono text-[14px] text-[#1a1d1a] bg-neutral-100 rounded-lg px-3 py-2 text-center tracking-wider">{c}</div>
                    ))}
                  </div>
                )}
                {backupCodes && (
                  <p className="mt-3 text-[11.5px] text-amber-600">⚠️ Copiez-les maintenant : ils ne seront plus jamais réaffichés.</p>
                )}
                <Button type="button" data-testid="admin-generate-backup-codes-btn" onClick={generateBackupCodes} disabled={backupGen}
                  className="btn-anim mt-4 h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[13.5px] px-5">
                  {backupGen ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  {backupCodes ? 'Régénérer de nouveaux codes' : 'Générer mes codes de secours'}
                </Button>
              </div>
            </section>

            <section className="mt-10" data-testid="admin-kyc-section">
              <div className="flex items-center gap-2">
                <IdCard className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Vérifications KYC en attente ({kyc.length})</h2>
              </div>
              {kyc.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
                  <p className="text-[14px] text-neutral-600">Aucune vérification d'identité en attente.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {kyc.map((k) => (
                    <div key={k.cagnotteId} className="rounded-2xl bg-white border border-black/5 p-4 sm:p-5" data-testid="admin-kyc-row">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[#1a1d1a]">{k.title}</div>
                          <div className="text-[12px] text-neutral-500">Objectif : {Number(k.goal).toLocaleString('fr-FR')} FCFA · Organisateur : {k.owner?.name || '—'} {k.owner?.phone ? `(${k.owner.phone})` : ''}</div>
                          {k.kycStatus === 'rejected' && <span className="text-[11px] text-red-600">Précédemment refusé — nouveaux documents</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <button type="button" data-testid={`kyc-approve-${k.cagnotteId}`} onClick={() => decideKyc(k.cagnotteId, 'approve')}
                            className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors">
                            <Check className="w-3.5 h-3.5" /> Valider
                          </button>
                          <button type="button" data-testid={`kyc-reject-${k.cagnotteId}`} onClick={() => decideKyc(k.cagnotteId, 'reject')}
                            className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 transition-colors">
                            <X className="w-3.5 h-3.5" /> Refuser
                          </button>
                        </div>
                      </div>
                      {k.documents?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {k.documents.map((d, i) => (
                            <button key={d.id} type="button" data-testid={`kyc-view-doc-${d.id}`} onClick={() => viewKycDoc(d.id)}
                              className="inline-flex items-center gap-1 text-[11.5px] px-2.5 py-1.5 rounded-full bg-[#0e3a3a]/5 text-[#0e3a3a] hover:bg-[#0e3a3a]/10 transition-colors">
                              <Eye className="w-3.5 h-3.5" /> Document {i + 1}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-10" data-testid="admin-audit-section">
              <div className="flex items-center gap-2">
                <History className="w-4.5 h-4.5 text-[#d97757]" />
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Journal de sécurité ({audit.length})</h2>
              </div>
              {audit.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-10 text-center">
                  <p className="text-[14px] text-neutral-600">Aucun événement enregistré. Les connexions et actions administrateur apparaîtront ici.</p>
                </div>
              ) : (
                <div className="mt-4 rounded-2xl bg-white border border-black/5 divide-y divide-black/5 overflow-hidden">
                  {audit.map((a, i) => {
                    const isLogin = a.action === 'login';
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid="admin-audit-row">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLogin ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                          {isLogin ? <ShieldCheck className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13.5px] font-medium text-[#1a1d1a]">
                            {isLogin ? `Connexion admin${a.meta?.setup ? ' (configuration MFA)' : ''}` : `Suppression cagnotte${a.meta?.title ? ` — ${a.meta.title}` : ''}`}
                          </div>
                          <div className="text-[11.5px] text-neutral-500 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {a.country || '??'} · {a.ip}</span>
                          </div>
                        </div>
                        <div className="text-[11.5px] text-neutral-400 whitespace-nowrap">
                          {a.at ? new Date(a.at).toLocaleString('fr-FR') : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {deleteTarget && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="admin-delete-modal">
          <div className="w-full max-w-[400px] rounded-3xl bg-white p-6 text-center shadow-2xl">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center bg-red-50 text-red-600">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="mt-4 text-[17px] font-semibold text-[#1a1d1a]">Supprimer « {deleteTarget.title} » ?</h3>
            <p className="mt-2 text-[13px] text-neutral-600">
              Suppression administrative définitive (dons et commentaires inclus). Le créateur <span className="font-medium">{deleteTarget.ownerName || 'inconnu'}</span> sera notifié.
            </p>
            <div className="mt-5 flex gap-2">
              <button type="button" data-testid="admin-delete-cancel" onClick={() => setDeleteTarget(null)} className="flex-1 h-11 rounded-full bg-[#f5f4ef] text-[13.5px] font-medium text-neutral-700 hover:bg-neutral-200 transition-colors">Annuler</button>
              <button type="button" data-testid="admin-delete-confirm" onClick={confirmDelete} disabled={deleting} className="flex-1 h-11 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-[13.5px] font-medium flex items-center justify-center gap-1.5 transition-colors">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
